import {
  Button,
  EButtonColorVariant,
  EHeadingStyleVariant,
  EModalHeaderTextAlignVariant,
  EModalSize,
  EModalVariants,
  ETextStyleVariant,
  Heading,
  LinkButton,
  Modal as BonfireModal,
  Text,
} from '@outdoorsyco/bonfire';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { TripInsuranceSelector } from '@/components/bill/BillModuleProtection/TripInsuranceSelector';
import Divider from '@/components/switchback/Divider/Divider';
import Modal from '@/components/switchback/Modal';
import Radio from '@/components/switchback/Radio/Radio';
import { useToast } from '@/components/switchback/Toast/ToastProvider';
import { ProtectionDialogContent } from '@/components/ui/TripProtection/ProtectionDialogContent';
import { EProtectionLabel, ProtectionLabel } from '@/components/ui/TripProtection/ProtectionLabel';
import { ServiceIntegration } from '@/graphql/types-and-hooks';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { addBookingService, getBooking, removeBookingService } from '@/redux/modules/checkout';
import { getQuote } from '@/redux/modules/quote';
import { getBookingOrQuote } from '@/redux/selectors/bill/data';
import { getBookingId } from '@/redux/selectors/checkout/booking';
import { GENERIC_ERROR_MESSAGE } from '@/services/apiRequest';
import { IBookingService } from '@/services/types/booking/services';
import { EProtections } from '@/services/types/core/protections';
import { formatCurrency } from '@/utility/currency';
import { useThunkDispatch } from '@/utility/redux/dispatch';

interface IProtectionModalProps {
  isOpened: boolean;
  onClose: () => void;
  availableTripInsuranceService?: ServiceIntegration;
  addedTripInsuranceService?: IBookingService;
  availableRoamlyWeatherInsuranceService?: ServiceIntegration;
  addedRoamlyWeatherInsuranceService?: IBookingService;
  tripInsuranceServiceIsAdded?: boolean;
  weatherInsuranceServiceIsAdded?: boolean;
  onSubmit?: () => void;
  confirmLabel?: string;
  rentalId?: number;
  isCampsite?: boolean;
}

export const ProtectionModal: React.FC<IProtectionModalProps> = ({
  isOpened,
  onClose,
  availableTripInsuranceService,
  addedTripInsuranceService,
  availableRoamlyWeatherInsuranceService,
  addedRoamlyWeatherInsuranceService,
  tripInsuranceServiceIsAdded,
  weatherInsuranceServiceIsAdded,
  onSubmit,
  confirmLabel,
  rentalId,
  isCampsite = false,
}) => {
  const intl = useIntl();
  const { isMobile } = useBreakpoint();
  const { addToast } = useToast();
  const dispatch = useThunkDispatch();
  const bookingId = useSelector(getBookingId);
  const bookingOrQuote = useSelector(getBookingOrQuote);
  const [submittingProtectionChange, setSubmittingProtectionChange] = useState(false);
  const [submittingWeatherProtectionChange, setSubmittingWeatherProtectionChange] = useState(false);
  const [addTripProtection, setAddTripProtection] = useState<boolean | undefined>(
    tripInsuranceServiceIsAdded,
  );
  const [addWeatherProtection, setAddWeatherProtection] = useState<boolean | undefined>(
    weatherInsuranceServiceIsAdded,
  );
  const [decliningProtection, setDecliningProtection] = useState(false);

  let hasProtectionInQuoteError = false;
  let hasWeatherProtectionInQuoteError = false;

  const toggleProtectionInQuote = async () => {
    if (!availableTripInsuranceService?.id) return;

    try {
      await dispatch(
        getQuote({
          serviceId: availableTripInsuranceService.id,
          serviceShouldBeAdded: addTripProtection,
          throwOnFetchFailure: true,
          rentalId,
        }),
      );
    } catch {
      hasProtectionInQuoteError = true;
      addToast({
        title: intl.formatMessage({ defaultMessage: 'Oops!', id: 'BEbOqj' }),
        description: GENERIC_ERROR_MESSAGE,
        type: 'TOAST_ERROR',
      });
    }
  };

  const toggleWeatherProtectionInQuote = async () => {
    if (!availableRoamlyWeatherInsuranceService?.id) return;

    try {
      await dispatch(
        getQuote({
          serviceId: availableRoamlyWeatherInsuranceService.id,
          serviceShouldBeAdded: addWeatherProtection,
          throwOnFetchFailure: true,
          rentalId,
        }),
      );
    } catch {
      hasWeatherProtectionInQuoteError = true;
      addToast({
        title: intl.formatMessage({ defaultMessage: 'Oops!', id: 'BEbOqj' }),
        description: GENERIC_ERROR_MESSAGE,
        type: 'TOAST_ERROR',
      });
    }
  };

  const toggleProtectionInBooking = async () => {
    if (!bookingId) return;

    try {
      if (addTripProtection) {
        if (availableTripInsuranceService?.id) {
          await dispatch(addBookingService(bookingId, availableTripInsuranceService.id));
        }

        return;
      }

      if (addedTripInsuranceService?.service_id) {
        await dispatch(removeBookingService(bookingId, addedTripInsuranceService.service_id));
      }
    } catch {
      addToast({
        title: intl.formatMessage({ defaultMessage: 'Oops!', id: 'BEbOqj' }),
        description: intl.formatMessage({
          defaultMessage: 'Your recent change is still processing. Try again in a minute or so.',
          id: 'Kk5UWs',
        }),
        type: 'TOAST_ERROR',
      });
    }
  };

  const toggleWeatherProtectionInBooking = async () => {
    if (!bookingId) return;

    try {
      if (addWeatherProtection) {
        if (availableRoamlyWeatherInsuranceService?.id) {
          const weatherQuote = availableRoamlyWeatherInsuranceService.roamly_weather_quote || {};
          await dispatch(
            addBookingService(bookingId, availableRoamlyWeatherInsuranceService.id, weatherQuote),
          );
        }

        return;
      }

      if (addedRoamlyWeatherInsuranceService?.service_id) {
        await dispatch(
          removeBookingService(bookingId, addedRoamlyWeatherInsuranceService.service_id),
        );
      }
    } catch {
      addToast({
        title: intl.formatMessage({ defaultMessage: 'Oops!', id: 'BEbOqj' }),
        description: intl.formatMessage({
          defaultMessage: 'Your recent change is still processing. Try again in a minute or so.',
          id: 'Kk5UWs',
        }),
        type: 'TOAST_ERROR',
      });
    }
  };

  const handleConfirmProtectionChange = async () => {
    try {
      setSubmittingProtectionChange(true);
      setSubmittingWeatherProtectionChange(true);

      // Toggle protection only if there are any actual changes.
      if (addTripProtection === !addedTripInsuranceService) {
        if (bookingId) {
          await toggleProtectionInBooking();
          await dispatch(getBooking(bookingId));
        } else {
          await toggleProtectionInQuote();
        }
      }

      if (addWeatherProtection === !addedRoamlyWeatherInsuranceService) {
        if (bookingId) {
          await toggleWeatherProtectionInBooking();
          await dispatch(getBooking(bookingId));
        } else {
          await toggleWeatherProtectionInQuote();
        }
      }
    } finally {
      setSubmittingProtectionChange(false);
      setSubmittingWeatherProtectionChange(false);
    }
    if (!hasProtectionInQuoteError && !hasWeatherProtectionInQuoteError && isCampsite) {
      hasProtectionInQuoteError = false;
      hasWeatherProtectionInQuoteError = false;
      onSubmit?.();
      return;
    }
    onSubmit?.();
    onClose();
  };

  useEffect(() => {
    if (addTripProtection === false || addWeatherProtection === false) {
      handleConfirmProtectionChange();
      setDecliningProtection(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decliningProtection]);

  const onDeclineProtection = () => {
    setAddTripProtection(false);
    setAddWeatherProtection(false);
    setDecliningProtection(true);
    onClose();
  };

  const onProtectionChange = (protection: string, _?: boolean) => {
    if (protection === 'trip') {
      setAddTripProtection(!addTripProtection);
    } else if (protection === 'weather') {
      setAddWeatherProtection(!addWeatherProtection);
    }
  };

  const tripInsurancePrice = formatCurrency({
    priceInCents: (addedTripInsuranceService || availableTripInsuranceService)?.total || 0,
    currency: bookingOrQuote?.presentment_currency || 'USD',
    digits: 2,
  });

  return (
    <>
      {availableTripInsuranceService ? (
        <Modal
          open={isOpened}
          onClose={onClose}
          title={
            <div className="text-lg text-gray-800 highlight">
              <ProtectionLabel
                category={EProtections.TRIP_INSURANCE}
                label={EProtectionLabel.TITLE}
              />
            </div>
          }
          footer={
            <>
              <Divider className="block mb-6" />

              <div className="flex items-center justify-end">
                <Button
                  className="w-auto"
                  variant={EButtonColorVariant.Primary}
                  label={
                    confirmLabel
                      ? confirmLabel
                      : intl.formatMessage({ defaultMessage: 'Confirm', id: 'N2IrpM' })
                  }
                  disabled={submittingProtectionChange || addTripProtection === undefined}
                  loading={submittingProtectionChange}
                  onClick={handleConfirmProtectionChange}
                />
              </div>
            </>
          }>
          <ProtectionDialogContent
            category={EProtections.TRIP_INSURANCE}
            serviceIsAdded
            inBillSection>
            <div className="flex flex-col md:flex-row">
              <Radio
                checked={addTripProtection === true}
                name="add-trip-protection"
                onChange={() => setAddTripProtection(true)}
                value="yes"
                className="flex-1 mb-2 md:mr-4 md:mb-0"
                outline>
                <p className="semiHighlight">
                  <FormattedMessage
                    defaultMessage="Yes, add trip protection for {price}"
                    id="5OtYHY"
                    values={{ price: tripInsurancePrice }}
                  />
                </p>
              </Radio>

              <Radio
                checked={addTripProtection === false}
                name="decline-trip-protection"
                onChange={() => setAddTripProtection(false)}
                value="no"
                className="flex-1"
                outline>
                <p className="semiHighlight">
                  <FormattedMessage
                    defaultMessage="No, I choose to decline this protection"
                    id="tl5/LK"
                  />
                </p>
              </Radio>
            </div>
          </ProtectionDialogContent>
        </Modal>
      ) : (
        <BonfireModal
          isOpen={isOpened}
          onClose={onClose}
          size={EModalSize.Medium}
          variant={isMobile ? EModalVariants.Bottom : undefined}>
          <BonfireModal.Actions />
          <BonfireModal.Header textAlign={EModalHeaderTextAlignVariant.Left} inlineHeader>
            <Heading variant={EHeadingStyleVariant.H5}>
              <FormattedMessage defaultMessage="Protect your trip" id="903/wm" />
            </Heading>
          </BonfireModal.Header>
          <BonfireModal.Content className="flex gap-6 max-h-[560px]">
            <Text variant={ETextStyleVariant.SmallRegular} className="text-gray-50">
              <FormattedMessage
                defaultMessage="Available to U.S. residents only. Add or cancel protection until the day before your trip. "
                id="nEtGHL"
              />
            </Text>
            <TripInsuranceSelector
              availableTripInsuranceService={availableTripInsuranceService}
              addedTripInsuranceService={addedTripInsuranceService}
              tripInsuranceServiceIsAdded={tripInsuranceServiceIsAdded}
              onChange={onProtectionChange}
            />
          </BonfireModal.Content>
          <BonfireModal.Divider className="mt-10" />
          <BonfireModal.Footer>
            <div className="flex items-center justify-between w-full">
              <LinkButton
                label={intl.formatMessage({ defaultMessage: 'Decline protection', id: '9BS4eG' })}
                onClick={onDeclineProtection}
              />
              <Button
                label={
                  confirmLabel
                    ? confirmLabel
                    : intl.formatMessage({ defaultMessage: 'Confirm', id: 'N2IrpM' })
                }
                disabled={
                  submittingProtectionChange ||
                  submittingWeatherProtectionChange ||
                  (addTripProtection === undefined && addWeatherProtection === undefined)
                }
                loading={submittingProtectionChange || submittingWeatherProtectionChange}
                onClick={handleConfirmProtectionChange}
                variant={EButtonColorVariant.Primary}
              />
            </div>
          </BonfireModal.Footer>
        </BonfireModal>
      )}
    </>
  );
};
